import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {useDispatch, useSelector} from 'react-redux';

import Utility from '../../../util/utilityfunctions';
import {currentResource, labels} from '../../../util/utils';
import {roles} from '../../../util/uiconstants';

import {getEngagementUser} from '../../../actions/EngagementUser/engagementUserAction';

import {IconoirMail, IconoirProhibition} from '@ey-xd/motif-icon';
import OutputText from '@ey/canvascoreservices/OutputText';

import ProgressLoader from '../../CoreWrappers/ProgressLoader';
import IconMessage from '../../CoreWrappers/IconMessage';
import Dropdown from '../../CoreWrappers/Dropdown';
import Button from '../../CoreWrappers/Button';
import Badge from '../../CoreWrappers/Badge';
import User from '../../CoreWrappers/User';
import StyledFullWidth from './StyledFullWidth';
import StyledUserGrid from './StyledUserGrid';
import StyledHeader from './StyledHeader';

const EngagementUsersDropdown = ({renderTrigger = null, engagementId}) => {
	const [isLoading, setIsLoading] = useState(true);
	const [isOpen, setIsOpen] = useState(false);

	const list = useSelector(({engagementUser}) => engagementUser[engagementId]);
	const projectId = Utility.getProjectIdFromUrl();

	const displayRoles = [
		roles.manager,
		roles.seniorManager,
		roles.executiveDirector,
		roles.principal,
		roles.otherPartner,
		roles.engagementPartner,
		roles.partnerInCharge
	];

	const dispatch = useDispatch();

	useEffect(() => {
		if (isOpen) {
			if (list) setIsLoading(false);
			else
				dispatch(getEngagementUser(engagementId, projectId)).finally(() => {
					setIsLoading(false);
				});
		}
	}, [engagementId, isOpen]);

	if (!renderTrigger) return false;

	const getValidUserEngagementRoleCount = (list) => {
		if (!list || !Array.isArray(list)) return 0;
		return list.filter((user) => user?.canvasEngagementRoleID && displayRoles.includes(user.canvasEngagementRoleID))
			.length;
	};

	const isEmpty = getValidUserEngagementRoleCount(list) === 0;

	const handleSendEmailAll = (e) => {
		e.preventDefault();
		e.stopPropagation();
		let emailList = '';
		list.forEach((user) => (emailList = emailList + user.emailAddress + ';'));
		Utility.sendEmails(emailList);
	};

	const handleSendEmail = (email) => Utility.sendEmails(email);

	const renderHeader = () => {
		const props = {
			type: 'button',
			variant: 'text-alt',
			label: labels.emailAll,
			size: 'small',
			onClick: handleSendEmailAll
		};

		return (
			<StyledHeader>
				<OutputText useTypography={false}>{labels.contacts}</OutputText>
				<Badge value={getValidUserEngagementRoleCount(list)} />
				{!isEmpty && !isLoading && <Button {...props} />}
			</StyledHeader>
		);
	};

	const renderLoading = () => (
		<StyledFullWidth>
			<ProgressLoader />
		</StyledFullWidth>
	);

	const renderEmpty = () => (
		<StyledFullWidth>
			<IconMessage title="" subTitle={labels.emptyMessage} icon={IconoirProhibition} />
		</StyledFullWidth>
	);

	const renderItem = ({user}) => {
		const renderTeams = () => {
			const props = {
				email: user?.emailAddress,
				firstName: user?.firstName,
				lastName: user?.lastName,
				showInfo: false,
				size: 'sm'
			};

			return <User {...props} />;
		};

		const renderRole = () => {
			const role = currentResource.roleForMember.find((x) => x.id === user?.canvasEngagementRoleID);
			const text = role ? role.label : '';
			return (
				<OutputText useTypography={false} title={text} ellipse>
					{text}
				</OutputText>
			);
		};

		const renderEmail = () => {
			const props = {
				type: 'button',
				variant: 'ghost',
				size: 'small',
				svg: IconoirMail,
				iconSize: 20,
				title: labels.email,
				onClick: (e) => {
					e.preventDefault();
					e.stopPropagation();
					handleSendEmail(user?.emailAddress);
				}
			};

			return <Button {...props} />;
		};

		return (
			<StyledUserGrid>
				{renderTeams()}
				{renderRole()}
				{renderEmail()}
			</StyledUserGrid>
		);
	};

	let items = [{id: 'loading', type: 'container', customRender: renderLoading}];

	if (!isLoading) {
		if (isEmpty) items = [{id: 'empty', type: 'container', customRender: renderEmpty}];
		else
			items = list
				.filter(({canvasEngagementRoleID}) => displayRoles.includes(canvasEngagementRoleID))
				.map((user) => ({
					id: user.employeeGUIID,
					type: 'container',
					customRender: renderItem,
					user
				}));
	}

	const handleOpenDropdown = () => {
		setIsOpen(!isOpen);
	};

	const props = {
		placement: 'bottom-right',
		trigger: renderTrigger(),
		items,
		topfix: true,
		reversed: true,
		maxItems: 3,
		itemSize: 52,
		handleOnClose: handleOpenDropdown,
		handleOnOpen: handleOpenDropdown,
		header: renderHeader
	};

	return <Dropdown {...props} />;
};

EngagementUsersDropdown.propTypes = {
	renderTrigger: PropTypes.func.isRequired,
	engagementId: PropTypes.number.isRequired // The component supports 1 engagement id per implementation.
};

export default EngagementUsersDropdown;
